<template>
    <div>
        <b-button variant="success" @click="attemptCheckout" v-bind="$attrs">
            <div v-if="totalAmount" class="d-flex align-items-center justify-content-center">
                <slot>
                    <span>{{ $t('buy') }} {{ isProrated ? proratedAmount : totalAmount }} {{ price.getCurrency() }}</span>
                    <b-badge 
                        v-if="isProrated"
                        variant="warning"
                        class="ml-2"
                        style="font-size: 0.75em;"
                        :id="`checkout-prorated-badge-${_uid}`"
                    >
                        {{ $t('checkout.prorated') }}
                    </b-badge>
                    <b-tooltip 
                        v-if="isProrated"
                        :target="`checkout-prorated-badge-${_uid}`"
                        triggers="hover"
                    >
                        {{ $t('checkout.proratedTooltip') }}
                    </b-tooltip>
                </slot>
            </div>

            <b-spinner v-else small></b-spinner>
        </b-button>

        <!-- Billing Information Modal -->
        <main-dialog
            :show="showBillingInformationModal"
            :title="$t('billingInformation')"
            @close="onBillingModalHidden"
        >
            <BillingInfo @saved="onBillingInfoSaved" />
        </main-dialog>
    </div>
</template>
<script>
import Checkout from '@/lib/Checkout'
import Price from '@/plugins/protokol/lib/Price'
import BillingInfo from '@/views/users/BillingInfo.vue'
import Env from '@/plugins/protokol/lib/Env'

// Global cache for price data shared across all CheckoutButton instances
const priceCache = {}

export default {
    name: 'CheckoutButton',
    components: {
        BillingInfo
    },
    props: {
        productType: {
            type: String
        },
        
        reference: {
            type: String
        },
        
        options: {
            type: Object
        },

        source: {
            type: String,
            default: 'platform'
        },
    },

    data: function() {
        return {
            showBillingInformationModal: false,
            billingInfoSaved: false,
            price: null,
            totalAmount: null,
            proratedAmount: null,
            isProrated: false,
            pendingCheckout: false,
        }
    },

    computed: {
        billingCountry() {
            const user = this.$store.getters.getUser
            const address = user && user.settings && user.settings.billing_address
            return address ? address.country : null
        }
    },

    watch: {
        billingCountry(newCountry) {
            this.price = new Price(newCountry || 'en_US', this.$store.getters.getLocale)
            // Clear cache entries for old currency so prices are re-fetched
            const newCurrency = this.price.getCurrency()
            Object.keys(priceCache).forEach(key => {
                if (!key.endsWith('-' + newCurrency)) {
                    delete priceCache[key]
                }
            })
            this.totalAmount = null
            this.proratedAmount = null
            this.fetchPrices()
        },
        options: {
            handler() {
                this.fetchPrices()
            },
            deep: true
        }
    },

    methods: {
        fetchPrices() {
            if (!this.price) {
                const user = this.$store.getters.getUser
                const billingAddress = user && user.settings && user.settings.billing_address
                const country = billingAddress ? billingAddress.country : 'en_US'
                this.price = new Price(country, this.$store.getters.getLocale)
            }
            
            const variant = this.options && this.options.variant ? this.options.variant : null
            const currency = this.price.getCurrency()
            
            // Create cache key based on product, reference, currency, and variant
            const variantPart = variant ? `-${variant}` : ''
            const cacheKey = `${this.productType}-${this.reference}-${currency}${variantPart}`
            
            // Check if we have cached price data
            if (priceCache[cacheKey]) {
                const cached = priceCache[cacheKey]
                this.totalAmount = cached.totalAmount
                this.proratedAmount = cached.proratedAmount
                this.isProrated = cached.isProrated
                return
            }
            
            // Fetch from API if not cached
            this.$transactions.getPricesForProduct(
                this.productType, 
                this.reference, 
                currency,
                variant
            ).then((resp) => {
                const totalAmount = resp.data.total_amount.toFixed(2)
                const proratedAmount = resp.data.prorated_amount.toFixed(2)
                const isProrated = totalAmount !== proratedAmount
                
                // Store in component state
                this.totalAmount = totalAmount
                this.proratedAmount = proratedAmount
                this.isProrated = isProrated
                
                // Cache the results
                priceCache[cacheKey] = {
                    totalAmount,
                    proratedAmount,
                    isProrated
                }
            }).catch((err) => {
                console.error('Failed to fetch prices:', err)
            })
        },
        
        validateBillingInfo() {
            const user = this.$store.getters.getUser
            if (!user || !user.settings || !user.settings.billing_address) {
                return false
            }

            const billing = user.settings.billing_address
            
            // Check required fields based on type
            if (billing.type === 'person') {
                return !!(
                    billing.country &&
                    billing.firstname &&
                    billing.lastname &&
                    billing.address &&
                    billing.city &&
                    billing.email
                )
            } else if (billing.type === 'company') {
                return !!(
                    billing.country &&
                    billing.company &&
                    billing.address &&
                    billing.city
                )
            }

            return false
        },

        onBillingInfoSaved() {
            this.billingInfoSaved = true
            this.showBillingInformationModal = false
            
            // Refresh user data
            this.$store.dispatch('setUser').then(() => {
                // Proceed with checkout after billing info is saved
                if (this.pendingCheckout) {
                    this.pendingCheckout = false
                    this.goToCheckout()
                }
            })
        },

        onBillingModalHidden() {
            // Reset pending checkout if modal is closed without saving
            this.pendingCheckout = false
        },

        attemptCheckout() {
            // Check if billing info is valid
            if (!this.validateBillingInfo()) {
                this.$bvToast.toast(this.$t('checkout.completeBillingInfo'), {
                    title: this.$t('checkout.billingRequired'),
                    variant: 'warning',
                    solid: true
                })
                this.pendingCheckout = true
                this.showBillingInformationModal = true
                return
            }

            this.goToCheckout()
        },

        async goToCheckout() {
            const popup = this.$loadingPopup()

            try {
                // Fetch project info to check if bank payment is allowed
                await this.$store.dispatch('fetchProjectInfo')
                const projectInfo = this.$store.getters.getProjectInfo
                
                // Build available payment methods based on global feature flags and project settings
                const cardPaymentsEnabled = Env.get('CARD_PAYMENTS_ENABLED') === true || Env.get('CARD_PAYMENTS_ENABLED') === 'true'
                const availablePaymentMethods = []
                if (cardPaymentsEnabled) {
                    availablePaymentMethods.push('card')
                }
                if (projectInfo && projectInfo.allow_bank_payment === true) {
                    availablePaymentMethods.push('bankaccount')
                }

                if (availablePaymentMethods.length === 0) {
                    this.$bvToast.toast(this.$t('countryNotSupported'), {
                        title: this.$t('error'),
                        variant: 'danger'
                    })
                    return
                }

                let checkout = new Checkout()
                const link = await checkout.getCheckoutLink({
                    product_type: this.productType,
                    reference: this.reference,
                    lang: this.$store.getters.getLocale,
                    options: this.options,
                    currency: this.price.getCurrency(),
                    available_payment_methods: availablePaymentMethods,
                })

                window.open(link.data, '_blank')
            } catch (err) {
                if (err.response) {
                    if (err.response.status == 501) {
                        this.$bvToast.toast(this.$t('countryNotSupported'), {
                            title: this.$t('error'),
                            variant: 'danger'
                        })
                        return 
                    }
                }

                this.$bvToast.toast(this.$t('failedToCheckout'), {
                    title: this.$t('error'),
                    variant: 'danger'
                })

                this.$debugLog(err)
            } finally {
                popup.hide()
            }

        }
    },

    mounted() {
        const user = this.$store.getters.getUser
        const billingAddress = user && user.settings && user.settings.billing_address
        const country = billingAddress ? billingAddress.country : 'en_US'
        
        this.price = new Price(country, this.$store.getters.getLocale)
        this.fetchPrices()
    }
}
</script>